import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, ArrowRight, FileText, Building2, Mail } from 'lucide-react';
import InvoiceRequestDialog from '@/components/InvoiceRequestDialog';

type BillingCycle = 'monthly' | 'annual';

interface Plan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: string;
  annualPrice: string;
  annualSaving?: string;
  monthlyPriceId: string | null;
  annualPriceId: string | null;
  features: string[];
  highlighted?: boolean;
  badge?: string;
  cta: string;
  individualRoute?: boolean;
  schoolRoute?: boolean;
  freeRoute?: boolean;
}

const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free Individual',
    description: 'Try the core evidence-led assessment workflow for free.',
    monthlyPrice: '£0',
    annualPrice: '£0',
    monthlyPriceId: null,
    annualPriceId: null,
    features: [
      '10 assessments/month',
      '1 class',
      '10 pupils',
      'Evidence-led writing judgements',
      'Multi-page writing uploads',
      'Progress tracking',
    ],
    cta: 'Get started free',
    freeRoute: true,
  },
  {
    id: 'individual',
    name: 'Individual',
    description: 'For teachers who need more assessments, clearer records, and stronger evidence for writing judgements.',
    monthlyPrice: '£9/mo',
    annualPrice: '£90/yr',
    annualSaving: 'Save £18',
    monthlyPriceId: 'price_1TRq1NKHBfrv5p55TJYmuMXW',
    annualPriceId: 'price_1TRq1NKHBfrv5p555RhA4yYr',
    features: [
      '50 assessments/month',
      '2 classes',
      '40 pupils',
      'Evidence-led writing judgements',
      'Multi-page writing uploads',
      'Draft report comments',
      'Progress tracking and assessment records',
      'CSV pupil import',
    ],
    highlighted: true,
    badge: 'Most popular',
    cta: 'Get started',
    individualRoute: true,
  },
  {
    id: 'starter',
    name: 'School Starter',
    description: 'For small primary schools building consistent writing assessment practice.',
    monthlyPrice: '£49/mo',
    annualPrice: '£490/yr',
    monthlyPriceId: 'price_1TRq1PKHBfrv5p558aMxk9s2',
    annualPriceId: 'price_1TRq1PKHBfrv5p550u2sotCa',
    features: [
      '200 assessments/month',
      'Up to 10 teachers',
      'School-wide pupil records',
      'Multi-page writing uploads',
      'Draft report comments',
      'Invite & manage teachers',
      'School leader assessment overview',
      'Key stage organisation',
    ],
    cta: 'Get started',
    schoolRoute: true,
  },
  {
    id: 'standard',
    name: 'School Standard',
    description: 'For primary schools securing consistent judgements across year groups and classes.',
    monthlyPrice: '£89/mo',
    annualPrice: '£890/yr',
    monthlyPriceId: 'price_1TRq1PKHBfrv5p55dxh2IL4f',
    annualPriceId: 'price_1TRq1PKHBfrv5p556fMyL98e',
    features: [
      '500 assessments/month',
      'Up to 30 teachers',
      'School-wide pupil records',
      'Multi-page writing uploads',
      'Draft report comments',
      'Invite & manage teachers',
      'School leader assessment overview',
      'Key stage organisation',
    ],
    cta: 'Get started',
    schoolRoute: true,
  },
  {
    id: 'plus',
    name: 'School Plus',
    description: 'For larger primary schools managing assessment consistency across more teachers, pupils, and classes.',
    monthlyPrice: '£149/mo',
    annualPrice: '£1,490/yr',
    monthlyPriceId: 'price_1TRq1PKHBfrv5p55cDKpiRle',
    annualPriceId: 'price_1TRq1PKHBfrv5p55nXrBHQCp',
    features: [
      '1,000 assessments/month',
      'Unlimited teachers',
      'School-wide pupil records',
      'Multi-page writing uploads',
      'Draft report comments',
      'Invite & manage teachers',
      'School leader assessment overview',
      'Key stage organisation',
    ],
    cta: 'Get started',
    schoolRoute: true,
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [invoiceDialog, setInvoiceDialog] = useState<{ plan: Plan; priceId: string } | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkSession();
  }, []);

  const handleCta = (plan: Plan) => {
    if (plan.freeRoute) {
      navigate('/auth?plan=individual');
      return;
    }
    if (!isAuthenticated) {
      navigate(plan.schoolRoute ? '/auth?plan=school' : '/auth?plan=individual');
      return;
    }
    if (plan.individualRoute) {
      navigate('/individual-billing');
    } else if (plan.schoolRoute) {
      navigate('/school-billing');
    }
  };

  const handleRequestInvoice = (plan: Plan) => {
    if (!isAuthenticated) {
      navigate('/auth?plan=school');
      return;
    }
    const priceId = billingCycle === 'monthly' ? plan.monthlyPriceId! : plan.annualPriceId!;
    setInvoiceDialog({ plan, priceId });
  };

  return (
    <div className="min-h-screen bg-background py-16 px-6">
      <SeoHead
        title="Pricing for Teachers, Schools and Trusts"
        description="Choose a WriteAssess plan for individual teachers, schools, or trusts. Secure writing judgements, strengthen moderation, and keep evidence in one place."
        canonical="/pricing"
      />
      <div className="max-w-6xl mx-auto space-y-10">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-4xl font-bold tracking-tight">Plans for teachers, schools and trusts</h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Start free as an individual teacher, then upgrade when you need more assessments, shared records, or school-wide moderation tools.
          </p>
        </div>

        {/* Monthly / Annual toggle */}
        <div className="flex justify-center">
          <div className="inline-flex items-center rounded-full border bg-muted/40 p-1 gap-1">
            <button
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-background shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-background shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setBillingCycle('annual')}
            >
              Annual
              <span className="ml-2 text-xs text-primary font-semibold">Save up to 17%</span>
            </button>
          </div>
        </div>

        {/* Plan cards — Free + Individual row */}
        <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {PLANS.filter((p) => p.id === 'free' || p.id === 'individual').map((plan) => (
            <Card
              key={plan.id}
              className={`relative shadow-soft border-2 flex flex-col ${
                plan.highlighted
                  ? 'border-primary'
                  : 'border-border hover:border-primary/50 transition-colors'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    {plan.badge}
                  </span>
                </div>
              )}
              <CardHeader className="pb-3 pt-6">
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <div className="mt-2">
                  <span className="text-3xl font-bold">
                    {billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice}
                  </span>
                  {plan.annualSaving && billingCycle === 'annual' && (
                    <span className="ml-2 text-sm text-primary font-medium">{plan.annualSaving}</span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex flex-col flex-1 gap-4">
                <ul className="space-y-2 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={plan.highlighted ? 'default' : 'outline'}
                  onClick={() => handleCta(plan)}
                >
                  {plan.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* School tier cards */}
        <div>
          <h2 className="text-center text-xl font-semibold mb-6">School plans</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {PLANS.filter((p) => p.schoolRoute).map((plan) => (
              <Card
                key={plan.id}
                className="shadow-soft border-2 border-border hover:border-primary/50 transition-colors flex flex-col"
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-2">
                    <span className="text-2xl font-bold">
                      {billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col flex-1 gap-4">
                  <ul className="space-y-2 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Check className="h-4 w-4 text-primary shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col gap-2">
                    <Button className="w-full" onClick={() => handleCta(plan)}>
                      {plan.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => handleRequestInvoice(plan)}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Request Invoice
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* MAT / Trust section */}
        <div>
          <h2 className="text-center text-xl font-semibold mb-6">Multi-academy trust plans</h2>
          <Card className="border-2 border-primary/20 bg-primary/5 shadow-soft">
            <CardContent className="flex flex-col md:flex-row items-start md:items-center gap-8 py-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-5 w-5 text-primary shrink-0" />
                  <h3 className="text-xl font-bold">Trust-wide moderation tools</h3>
                  <span className="bg-primary text-primary-foreground text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap">Custom pricing</span>
                </div>
                <p className="text-muted-foreground text-sm mb-5 max-w-2xl">
                  Give trust leaders a clearer view of writing judgements across every school, with tools to strengthen moderation, compare standards, and report consistently.
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                  {[
                    'Trust-wide assessment overview',
                    'Compare writing judgements across schools',
                    'Strengthen moderation across the trust',
                    'Trust-level reports and exports',
                    'Central billing and procurement',
                    'Dedicated onboarding and account management',
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="shrink-0 flex flex-col gap-3 md:items-center">
                <p className="text-2xl font-bold text-foreground">Custom</p>
                <p className="text-xs text-muted-foreground text-center">Pricing based on the number of schools</p>
                <Button onClick={() => navigate('/contact')} className="gap-2 w-full md:w-auto">
                  <Mail className="h-4 w-4" />
                  Discuss trust pricing
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* VAT notice */}
        <p className="text-center text-xs text-muted-foreground">
          WriteAssess is not currently VAT registered, so VAT is not charged. Prices shown are the total amount you pay. This may be reviewed in future.
        </p>

        {/* Footer sign-in prompt */}
        {!isAuthenticated && (
          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <button
              className="text-primary font-medium hover:underline"
              onClick={() => navigate('/auth')}
            >
              Sign in →
            </button>
          </p>
        )}
      </div>

      {invoiceDialog && (
        <InvoiceRequestDialog
          open={true}
          onOpenChange={(open) => { if (!open) setInvoiceDialog(null); }}
          priceId={invoiceDialog.priceId}
          planName={invoiceDialog.plan.name}
        />
      )}
    </div>
  );
}
